import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';

import type {
  ScheduledItemSummary,
  TypeDefaults,
  TypeDefaultsField,
  TypeDefaultsPreviewProps,
} from './TypeDefaultsPreview.types';

const FIELD_LABELS: Record<TypeDefaultsField, string> = {
  template: 'Note template',
  duration: 'Duration',
  modality: 'Visit mode',
  placeOfService: 'Place of service',
  cptCodes: 'Billing codes',
  forms: 'Forms',
  reminders: 'Reminders',
};

const formatItems = (items: ScheduledItemSummary[]): string =>
  items
    .map((item) => (item.timing ? `${item.title} (${item.timing})` : item.title))
    .join(' · ');

interface FieldValue {
  field: TypeDefaultsField;
  /** Display value; null means "not set". */
  value: string | null;
  /** Short value for the inline variant (counts/abbreviations). */
  inlineValue: string | null;
}

const toFieldValues = (defaults: TypeDefaults): FieldValue[] => [
  {
    field: 'template',
    value: defaults.templateSet
      ? defaults.templateName ?? 'Template selected'
      : null,
    inlineValue: defaults.templateSet
      ? defaults.templateName ?? 'Template'
      : null,
  },
  {
    field: 'duration',
    value: defaults.durationLabel,
    inlineValue: defaults.durationLabel,
  },
  {
    field: 'modality',
    value: defaults.modalityLabel,
    inlineValue: defaults.modalityLabel,
  },
  {
    field: 'placeOfService',
    value: defaults.placeOfServiceLabel,
    inlineValue: defaults.placeOfServiceCode
      ? `POS ${defaults.placeOfServiceCode}`
      : null,
  },
  {
    field: 'cptCodes',
    value: defaults.cptCodes.length > 0 ? defaults.cptCodes.join(', ') : null,
    inlineValue:
      defaults.cptCodes.length > 0 ? `CPT ${defaults.cptCodes.join(', ')}` : null,
  },
  {
    field: 'forms',
    value: defaults.forms.length > 0 ? formatItems(defaults.forms) : null,
    inlineValue:
      defaults.forms.length > 0
        ? `${defaults.forms.length} form${defaults.forms.length === 1 ? '' : 's'}`
        : null,
  },
  {
    field: 'reminders',
    value: defaults.reminders.length > 0 ? formatItems(defaults.reminders) : null,
    inlineValue:
      defaults.reminders.length > 0
        ? `${defaults.reminders.length} reminder${
            defaults.reminders.length === 1 ? '' : 's'
          }`
        : null,
  },
];

const LoadingRows: React.FC = () => (
  <Stack spacing={0.75} aria-label="Loading appointment type defaults">
    <Skeleton variant="text" width="70%" />
    <Skeleton variant="text" width="55%" />
    <Skeleton variant="text" width="62%" />
  </Stack>
);

/**
 * Shared renderer for what an appointment type fills in on a visit — note
 * template, duration, visit mode, billing, forms & reminders. Presentation
 * only: no fetching, no store, no analytics (containers own those).
 *
 * Used by the encounter picker (A3, GDN-2230) and the cleanup wizard's
 * compare/confirm surfaces (GDN-2237/GDN-2238) via `highlightFields`.
 */
export const TypeDefaultsPreview: React.FC<TypeDefaultsPreviewProps> = ({
  defaults,
  variant = 'rows',
  highlightFields = [],
  loading = false,
  sx,
}) => {
  if (loading) return <LoadingRows />;

  const fields = toFieldValues(defaults);
  const highlighted = new Set<TypeDefaultsField>(highlightFields);
  const nothingSet = fields.every((field) => field.value === null);

  if (variant === 'inline') {
    const setFields = fields.filter((field) => field.inlineValue !== null);
    if (setFields.length === 0) {
      return (
        <Typography variant="caption" color="text.disabled">
          No visit defaults
        </Typography>
      );
    }
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', lineHeight: 1.5, ...sx }}
        aria-label="Appointment type defaults"
      >
        {setFields.map((field, index) => (
          <React.Fragment key={field.field}>
            {index > 0 && ' · '}
            <Box
              component="span"
              sx={
                highlighted.has(field.field)
                  ? { color: 'warning.dark', fontWeight: 600 }
                  : undefined
              }
            >
              {field.inlineValue}
            </Box>
          </React.Fragment>
        ))}
      </Typography>
    );
  }

  if (nothingSet) {
    return (
      <Typography variant="body2" color="text.secondary">
        No visit defaults yet — this type only names the visit.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75} sx={sx} aria-label="Appointment type defaults">
      {fields.map((field) => {
        const isHighlighted = highlighted.has(field.field);
        return (
          <Box
            key={field.field}
            sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 112, flexShrink: 0 }}
            >
              {FIELD_LABELS[field.field]}
            </Typography>
            <Typography
              variant="body2"
              color={
                field.value === null
                  ? 'text.disabled'
                  : isHighlighted
                    ? 'warning.dark'
                    : 'text.primary'
              }
              sx={{ fontWeight: isHighlighted ? 600 : 400 }}
            >
              {field.value ?? 'Not set'}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};
